import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Users, Gamepad2, CreditCard, Settings, ArrowLeft, Trash2, Coins, BarChart3, Crown, Loader2, RefreshCw, ShoppingBag, Plus, Edit, Key, Sparkles, X, ScrollText, Hourglass, Cpu, Music, LayoutGrid } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import AudioLibraryTab from "@/components/admin/AudioLibraryTab";
import LandingEditorTab from "@/components/admin/LandingEditorTab";
import DropRatesTab from "@/components/admin/DropRatesTab";

export default function AdminPage() {
  const { token, API, user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [games, setGames] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("dashboard");
  const [dailyPages, setDailyPages] = useState("");
  const [addGoldUserId, setAddGoldUserId] = useState("");
  const [addGoldAmount, setAddGoldAmount] = useState("");
  const [usersTotal, setUsersTotal] = useState(0);
  const [gamesTotal, setGamesTotal] = useState(0);
  const [txTotal, setTxTotal] = useState(0);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [customItems, setCustomItems] = useState([]);
  const [allShopItems, setAllShopItems] = useState([]); // built-in + custom + overrides applied
  const [editingItem, setEditingItem] = useState(null); // {id, draft}
  const [aiUsage, setAiUsage] = useState(null);
  const [aiUsageDays, setAiUsageDays] = useState(7);
  const [loadingAiUsage, setLoadingAiUsage] = useState(false);
  const [selectedAiUser, setSelectedAiUser] = useState(null);
  const [newItem, setNewItem] = useState({
    name: "", category: "potions", price: 50, item_type: "ability", description: "",
    attack: 0, defense: 0, hp: 0, max_hp: 0, mana_cost: 1, effect: "none", effect_value: 0,
    equippable: false, slot: "none", rarity: "common", foil: false,
    available_from: "", available_until: ""
  });
  const [editingAvailability, setEditingAvailability] = useState(null); // item id being edited
  const [availabilityForm, setAvailabilityForm] = useState({ available_from: "", available_until: "" });
  const [classData, setClassData] = useState(null);
  const [editingClass, setEditingClass] = useState(null);
  const [editingEvo, setEditingEvo] = useState(null);
  const [evoLevels, setEvoLevels] = useState("");
  // New: edit state for user admin actions and character edit
  const [editingUserId, setEditingUserId] = useState(null);
  const [userEditForm, setUserEditForm] = useState({ daily_pages: "", daily_limit_override: "", new_password: "" });
  const [editingCharGame, setEditingCharGame] = useState(null); // game object being edited
  const [charEditForm, setCharEditForm] = useState({});
  const [userGames, setUserGames] = useState({}); // { [user_id]: [games] }
  // Audit log
  const [auditEntries, setAuditEntries] = useState([]);
  const [auditTotal, setAuditTotal] = useState(0);
  const [auditFilterAction, setAuditFilterAction] = useState("all");

  const loadAuditLog = useCallback(async (filterAction = "all") => {
    try {
      const params = filterAction !== "all" ? `?action=${filterAction}&limit=200` : "?limit=200";
      const r = await axios.get(`${API}/admin/audit-log${params}`, { headers: { Authorization: `Bearer ${token}` } });
      setAuditEntries(r.data.entries);
      setAuditTotal(r.data.total);
    } catch { /* silent */ }
  }, [API, token]);

  const loadAiUsage = useCallback(async (d = 7) => {
    setLoadingAiUsage(true);
    setSelectedAiUser(null);
    try {
      const r = await axios.get(`${API}/admin/ai-usage?days=${d}`, { headers: { Authorization: `Bearer ${token}` } });
      setAiUsage(r.data);
    } catch { /* silent */ }
    finally { setLoadingAiUsage(false); }
  }, [API, token]);

  useEffect(() => {
    if (activeTab === "ai-usage" && !aiUsage) {
      loadAiUsage(aiUsageDays);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const getHeaders = () => ({ Authorization: `Bearer ${token}` });

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const h = { Authorization: `Bearer ${token}` };
      const [statsRes, usersRes, gamesRes, txRes, cfgRes, customRes, classRes, allShopRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { headers: h }),
        axios.get(`${API}/admin/users`, { headers: h }),
        axios.get(`${API}/admin/games`, { headers: h }),
        axios.get(`${API}/admin/transactions`, { headers: h }),
        axios.get(`${API}/admin/config`, { headers: h }),
        axios.get(`${API}/admin/custom-shop-items`, { headers: h }),
        axios.get(`${API}/admin/classes`, { headers: h }),
        axios.get(`${API}/admin/shop-items`, { headers: h }),
      ]);
      setStats(statsRes.data);
      setUsers(usersRes.data.users.sort((a, b) => (b.is_admin ? 1 : 0) - (a.is_admin ? 1 : 0)));
      setUsersTotal(usersRes.data.total);
      setGames(gamesRes.data.games);
      setGamesTotal(gamesRes.data.total);
      setTransactions(txRes.data.transactions);
      setTxTotal(txRes.data.total);
      setConfig(cfgRes.data);
      setDailyPages(String(cfgRes.data.daily_free_pages));
      setCustomItems(customRes.data);
      setAllShopItems(allShopRes.data);
      setClassData(classRes.data);
      setEvoLevels(classRes.data.evolution_levels?.join(", ") || "5, 10");
      // Load audit log (recent 200)
      loadAuditLog("all");
    } catch (e) {
      if (e.response?.status === 403) {
        toast.error("Admin access required");
        navigate("/");
      }
    } finally {
      setLoading(false);
    }
  }, [API, token, navigate]);

  useEffect(() => { loadData(); }, []);

  const handleDeleteUser = async (userId) => {
    try {
      await axios.delete(`${API}/admin/users/${userId}`, { headers: getHeaders() });
      toast.success("User deleted");
      setConfirmDelete(null);
      loadData();
    } catch { toast.error("Failed"); }
  };

  const handleDeleteGame = async (gameId) => {
    try {
      await axios.delete(`${API}/admin/games/${gameId}`, { headers: getHeaders() });
      toast.success("Game deleted");
      setConfirmDelete(null);
      loadData();
    } catch { toast.error("Failed"); }
  };

  const handleUpdateConfig = async () => {
    try {
      await axios.post(`${API}/admin/config`, { daily_free_pages: parseInt(dailyPages) }, { headers: getHeaders() });
      toast.success("Config updated");
      loadData();
    } catch { toast.error("Failed"); }
  };

  const handleAddGold = async () => {
    if (!addGoldUserId || !addGoldAmount) return;
    try {
      await axios.post(`${API}/admin/add-gold`, { user_id: addGoldUserId, gold: parseInt(addGoldAmount) }, { headers: getHeaders() });
      toast.success(`Added ${addGoldAmount} gold`);
      setAddGoldUserId("");
      setAddGoldAmount("");
    } catch { toast.error("Failed"); }
  };

  const handleCreateShopItem = async () => {
    if (!newItem.name || !newItem.description) { toast.error("Name and description required"); return; }
    try {
      const payload = { ...newItem };
      // Keep availability fields as ISO strings or null
      payload.available_from = newItem.available_from || null;
      payload.available_until = newItem.available_until || null;
      await axios.post(`${API}/admin/custom-shop-item`, payload, { headers: getHeaders() });
      toast.success("Item created!");
      setNewItem({ name: "", category: "potions", price: 50, item_type: "ability", description: "", attack: 0, defense: 0, hp: 0, max_hp: 0, mana_cost: 1, effect: "none", effect_value: 0, equippable: false, slot: "none", rarity: "common", foil: false, available_from: "", available_until: "" });
      loadData();
    } catch { toast.error("Failed"); }
  };

  const handleDeleteCustomItem = async (itemId) => {
    try {
      await axios.delete(`${API}/admin/custom-shop-item/${itemId}`, { headers: getHeaders() });
      toast.success("Item deleted");
      setConfirmDelete(null);
      loadData();
    } catch { toast.error("Failed"); }
  };

  const handleUpdateItemAvailability = async (itemId, availableFrom, availableUntil) => {
    try {
      await axios.post(`${API}/admin/custom-shop-item/${itemId}/availability`, {
        available_from: availableFrom || null,
        available_until: availableUntil || null,
      }, { headers: getHeaders() });
      toast.success("Disponibilità aggiornata");
      loadData();
    } catch { toast.error("Aggiornamento fallito"); }
  };

  const handlePatchShopItem = async (itemId, patch) => {
    try {
      await axios.patch(`${API}/admin/shop-item/${itemId}`, patch, { headers: getHeaders() });
      toast.success("Item updated");
      setEditingItem(null);
      loadData();
    } catch (e) { toast.error(e.response?.data?.detail || "Update failed"); }
  };

  const handleResetShopItem = async (itemId) => {
    try {
      await axios.post(`${API}/admin/shop-item/${itemId}/reset`, {}, { headers: getHeaders() });
      toast.success("Reset to defaults");
      loadData();
    } catch (e) { toast.error(e.response?.data?.detail || "Reset failed"); }
  };

  const handleToggleShopItemDisabled = async (itemId, currentDisabled) => {
    try {
      await axios.patch(`${API}/admin/shop-item/${itemId}`, { disabled: !currentDisabled }, { headers: getHeaders() });
      toast.success(!currentDisabled ? "Item disabled" : "Item enabled");
      loadData();
    } catch (e) { toast.error(e.response?.data?.detail || "Toggle failed"); }
  };

  const handleSaveClassStats = async (className) => {
    if (!editingClass) return;
    try {
      await axios.post(`${API}/admin/class-stats`, { class_name: className, ...editingClass }, { headers: getHeaders() });
      toast.success(`${className} stats updated`);
      setEditingClass(null);
      loadData();
    } catch { toast.error("Failed"); }
  };

  const handleSaveEvolution = async (parentClass, statKey) => {
    if (!editingEvo) return;
    try {
      await axios.post(`${API}/admin/class-evolution`, {
        parent_class: parentClass, stat_key: statKey, ...editingEvo
      }, { headers: getHeaders() });
      toast.success("Evolution updated");
      setEditingEvo(null);
      loadData();
    } catch { toast.error("Failed"); }
  };

  const handleSaveEvoLevels = async () => {
    const levels = evoLevels.split(",").map(s => parseInt(s.trim())).filter(n => !isNaN(n) && n > 0);
    if (levels.length === 0) { toast.error("Enter at least one level"); return; }
    try {
      await axios.post(`${API}/admin/evolution-levels`, { levels }, { headers: getHeaders() });
      toast.success("Evolution levels updated");
      loadData();
    } catch { toast.error("Failed"); }
  };

  // ==== User management handlers ====
  const openUserEdit = (u) => {
    setEditingUserId(u.id);
    setUserEditForm({
      daily_pages: String(u.daily_pages_today ?? 0),
      daily_limit_override: u.daily_limit_override ? String(u.daily_limit_override) : "",
      new_password: "",
    });
    // Lazy-load games for drill-down
    if (!userGames[u.id]) {
      axios.get(`${API}/admin/users/${u.id}/games`, { headers: getHeaders() })
        .then(r => setUserGames(prev => ({ ...prev, [u.id]: r.data.games })))
        .catch(() => {});
    }
  };

  const handleSaveUserLimits = async (userId) => {
    try {
      const payload = {};
      if (userEditForm.daily_pages !== "") payload.daily_pages = parseInt(userEditForm.daily_pages) || 0;
      if (userEditForm.daily_limit_override !== "") payload.daily_limit_override = parseInt(userEditForm.daily_limit_override) || 0;
      else payload.daily_limit_override = 0; // clears override
      await axios.post(`${API}/admin/users/${userId}/limits`, payload, { headers: getHeaders() });
      toast.success("Limiti aggiornati");
      loadData();
    } catch { toast.error("Aggiornamento fallito"); }
  };

  const handleResetToday = async (userId) => {
    try {
      await axios.post(`${API}/admin/users/${userId}/limits`, { reset_today: true }, { headers: getHeaders() });
      toast.success("Generazioni odierne azzerate");
      loadData();
    } catch { toast.error("Reset fallito"); }
  };

  const handleToggleAdmin = async (userId, currentIsAdmin) => {
    if (userId === user?.id && currentIsAdmin) {
      toast.error("Non puoi revocare il tuo stesso ruolo admin");
      return;
    }
    try {
      await axios.post(`${API}/admin/users/${userId}/role`, { is_admin: !currentIsAdmin }, { headers: getHeaders() });
      toast.success(!currentIsAdmin ? "Privilegi Admin concessi" : "Privilegi Admin revocati");
      loadData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fallito");
    }
  };

  const handleResetPassword = async (userId) => {
    const pwd = userEditForm.new_password;
    if (!pwd || pwd.length < 4) { toast.error("Password minimo 4 caratteri"); return; }
    try {
      await axios.post(`${API}/admin/users/${userId}/password`, { password: pwd }, { headers: getHeaders() });
      toast.success("Password reimpostata");
      setUserEditForm({ ...userEditForm, new_password: "" });
    } catch { toast.error("Reset password fallito"); }
  };

  // ==== Character edit handlers ====
  const openCharEdit = (game) => {
    setEditingCharGame(game);
    const c = game.character || {};
    setCharEditForm({
      level: c.level ?? 1,
      xp: c.xp ?? 0,
      stat_points: c.stat_points ?? 0,
      gold: c.gold ?? 0,
      hp: c.hp ?? 0,
      max_hp: c.max_hp ?? 0,
      base_attack: c.base_attack ?? 0,
      base_defense: c.base_defense ?? 0,
      mana: c.mana ?? 0,
      max_mana: c.max_mana ?? 0,
    });
  };

  const handleSaveCharacter = async () => {
    if (!editingCharGame) return;
    try {
      const payload = {};
      Object.keys(charEditForm).forEach(k => {
        const v = charEditForm[k];
        if (v !== "" && v !== null && v !== undefined && !isNaN(parseInt(v))) payload[k] = parseInt(v);
      });
      await axios.post(`${API}/admin/games/${editingCharGame.id}/character`, payload, { headers: getHeaders() });
      toast.success(`Personaggio ${editingCharGame.character?.name} aggiornato`);
      setEditingCharGame(null);
      // Refresh games list and user drill-down cache
      loadData();
      if (editingCharGame.user_id) {
        axios.get(`${API}/admin/users/${editingCharGame.user_id}/games`, { headers: getHeaders() })
          .then(r => setUserGames(prev => ({ ...prev, [editingCharGame.user_id]: r.data.games })))
          .catch(() => {});
      }
    } catch { toast.error("Aggiornamento personaggio fallito"); }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0c] flex items-center justify-center">
        <Loader2 size={40} className="text-[#cfb53b] animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0c] text-[#e2e8f0]" data-testid="admin-page">
      {/* Header */}
      <div className="border-b border-[#cfb53b]/20 bg-[#1a1a1d]/80 px-3 sm:px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2 sm:gap-3 min-w-0">
          <Button variant="ghost" size="sm" onClick={() => navigate("/")} className="text-[#94a3b8] shrink-0 h-8 w-8 p-0">
            <ArrowLeft size={16} />
          </Button>
          <h1 className="font-heading text-base sm:text-lg text-[#cfb53b] flex items-center gap-2 truncate">
            <Crown size={16} className="shrink-0" /> <span>Admin Panel</span>
          </h1>
        </div>
        <Button variant="ghost" size="sm" onClick={loadData} className="text-[#94a3b8] hover:text-[#cfb53b] shrink-0 px-2 sm:px-3">
          <RefreshCw size={14} className="sm:mr-1" /> <span className="hidden sm:inline">Refresh</span>
        </Button>
      </div>

      <div className="max-w-6xl mx-auto p-2 sm:p-4">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <div className="mb-4 sm:mb-6">
            <TabsList className="bg-[#1a1a1d] border border-[#cfb53b]/20 flex flex-wrap h-auto w-full justify-start gap-1 p-1">
              <TabsTrigger value="dashboard" className="data-[state=active]:bg-[#cfb53b]/10 data-[state=active]:text-[#cfb53b] whitespace-nowrap">
                <BarChart3 size={14} className="mr-1.5" /> Dashboard
              </TabsTrigger>
              <TabsTrigger value="users" className="data-[state=active]:bg-[#cfb53b]/10 data-[state=active]:text-[#cfb53b] whitespace-nowrap">
                <Users size={14} className="mr-1.5" /> Users ({usersTotal})
              </TabsTrigger>
              <TabsTrigger value="games" className="data-[state=active]:bg-[#cfb53b]/10 data-[state=active]:text-[#cfb53b] whitespace-nowrap">
                <Gamepad2 size={14} className="mr-1.5" /> Games ({gamesTotal})
              </TabsTrigger>
              <TabsTrigger value="payments" className="data-[state=active]:bg-[#cfb53b]/10 data-[state=active]:text-[#cfb53b] whitespace-nowrap">
                <CreditCard size={14} className="mr-1.5" /> Payments ({txTotal})
              </TabsTrigger>
              <TabsTrigger value="config" className="data-[state=active]:bg-[#cfb53b]/10 data-[state=active]:text-[#cfb53b] whitespace-nowrap">
                <Settings size={14} className="mr-1.5" /> Config
            </TabsTrigger>
            <TabsTrigger value="shop-editor" className="data-[state=active]:bg-[#cfb53b]/10 data-[state=active]:text-[#cfb53b] whitespace-nowrap">
              <ShoppingBag size={14} className="mr-1.5" /> Shop
            </TabsTrigger>
            <TabsTrigger value="classes" className="data-[state=active]:bg-[#cfb53b]/10 data-[state=active]:text-[#cfb53b] whitespace-nowrap">
              <Crown size={14} className="mr-1.5" /> Classes
            </TabsTrigger>
            <TabsTrigger value="audit" className="data-[state=active]:bg-[#cfb53b]/10 data-[state=active]:text-[#cfb53b] whitespace-nowrap" data-testid="audit-tab-trigger">
              <ScrollText size={14} className="mr-1.5" /> Audit ({auditTotal})
            </TabsTrigger>
            <TabsTrigger value="ai-usage" className="data-[state=active]:bg-[#cfb53b]/10 data-[state=active]:text-[#cfb53b] whitespace-nowrap" data-testid="ai-usage-tab-trigger">
              <Cpu size={14} className="mr-1.5" /> AI Usage
            </TabsTrigger>
            <TabsTrigger value="audio" className="data-[state=active]:bg-[#cfb53b]/10 data-[state=active]:text-[#cfb53b] whitespace-nowrap" data-testid="audio-tab-trigger">
              <Music size={14} className="mr-1.5" /> Audio
            </TabsTrigger>
            <TabsTrigger value="landing" className="data-[state=active]:bg-[#cfb53b]/10 data-[state=active]:text-[#cfb53b] whitespace-nowrap" data-testid="landing-tab-trigger">
              <LayoutGrid size={14} className="mr-1.5" /> Landing
            </TabsTrigger>
            <TabsTrigger value="drop-rates" className="data-[state=active]:bg-[#cfb53b]/10 data-[state=active]:text-[#cfb53b] whitespace-nowrap" data-testid="drop-rates-tab-trigger">
              <Sparkles size={14} className="mr-1.5" /> Drop Rates
            </TabsTrigger>
            </TabsList>
          </div>

          {/* Dashboard */}
          <TabsContent value="dashboard">
            {stats && (
              <div className="space-y-6">
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                  {[
                    { label: "Users", value: stats.total_users, color: "text-blue-400" },
                    { label: "Games", value: stats.total_games, color: "text-green-400" },
                    { label: "Premium", value: stats.premium_users, color: "text-[#cfb53b]" },
                    { label: "Payments", value: stats.paid_payments, color: "text-purple-400" },
                    { label: "Revenue", value: `$${stats.total_revenue}`, color: "text-green-400" },
                    { label: "Transactions", value: stats.total_payments, color: "text-[#94a3b8]" },
                  ].map((s, i) => (
                    <div key={i} className="bg-[#1a1a1d]/80 border border-[#2d3748] rounded-lg p-4 text-center">
                      <p className={`font-heading text-2xl ${s.color}`}>{s.value}</p>
                      <p className="text-xs text-[#94a3b8] mt-1">{s.label}</p>
                    </div>
                  ))}
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <div className="bg-[#1a1a1d]/80 border border-[#2d3748] rounded-lg p-4">
                    <h3 className="font-heading text-sm text-[#cfb53b] mb-3">Recent Users</h3>
                    <div className="space-y-2">
                      {stats.recent_users.map((u) => (
                        <div key={u.id || u.email} className="flex justify-between text-xs border-b border-[#2d3748] pb-1.5">
                          <span className="text-[#e2e8f0]">{u.username} <span className="text-[#64748b]">({u.email})</span></span>
                          <span className="text-[#64748b]">{u.created_at?.slice(0, 10)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="bg-[#1a1a1d]/80 border border-[#2d3748] rounded-lg p-4">
                    <h3 className="font-heading text-sm text-[#cfb53b] mb-3">Recent Games</h3>
                    <div className="space-y-2">
                      {stats.recent_games.map((g) => (
                        <div key={g.id || `${g.character?.name}-${g.created_at}`} className="flex justify-between text-xs border-b border-[#2d3748] pb-1.5">
                          <span className="text-[#e2e8f0]">{g.character?.name} <span className="text-[#64748b]">Lv.{g.character?.level} Ch.{g.current_chapter}</span></span>
                          <span className="text-[#64748b]">{g.setting} | {g.created_at?.slice(0, 10)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </TabsContent>

          {/* Users */}
          <TabsContent value="users">
            <div className="bg-[#1a1a1d]/80 border border-[#2d3748] rounded-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-xs" data-testid="admin-users-table">
                  <thead className="bg-[#0a0a0c] text-[#94a3b8]">
                    <tr>
                      <th className="px-3 py-2 text-left font-heading">ID</th>
                      <th className="px-3 py-2 text-left font-heading">Username</th>
                      <th className="px-3 py-2 text-left font-heading">Email</th>
                      <th className="px-3 py-2 text-left font-heading">Gold</th>
                      <th className="px-3 py-2 text-left font-heading" title="Generazioni AI usate oggi / limite">Gen. Oggi</th>
                      <th className="px-3 py-2 text-left font-heading">Giochi</th>
                      <th className="px-3 py-2 text-left font-heading">Admin</th>
                      <th className="px-3 py-2 text-left font-heading">Azioni</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map(u => {
                      const isEditing = editingUserId === u.id;
                      const limit = u.daily_limit_override || u.daily_limit_effective || config?.daily_free_pages || 10;
                      const used = u.daily_pages_today || 0;
                      const ratioColor = used >= limit ? "text-red-400" : used >= limit * 0.7 ? "text-yellow-400" : "text-green-400";
                      return (
                      <React.Fragment key={u.id}>
                      <tr className="border-t border-[#2d3748] hover:bg-[#0a0a0c]/50">
                        <td className="px-3 py-2 text-[#64748b] font-mono text-[10px] cursor-pointer hover:text-[#cfb53b] select-all" title="Click to copy full ID" onClick={() => {
                          const text = u.id;
                          try {
                            const ta = document.createElement('textarea');
                            ta.value = text;
                            ta.style.position = 'fixed';
                            ta.style.opacity = '0';
                            document.body.appendChild(ta);
                            ta.select();
                            document.execCommand('copy');
                            document.body.removeChild(ta);
                            toast.success(`ID copiato: ${text}`);
                          } catch { toast.error("Copy failed - ID: " + text); }
                        }}>{u.id?.slice(0, 8)}...</td>
                        <td className="px-3 py-2 text-[#e2e8f0]">{u.username}</td>
                        <td className="px-3 py-2 text-[#94a3b8]">{u.email}</td>
                        <td className="px-3 py-2"><span className="text-[#cfb53b]">{u.premium_gold || 0}</span></td>
                        <td className="px-3 py-2">
                          <span className={ratioColor} data-testid={`user-gen-${u.id}`}>{used}/{limit}</span>
                          {u.daily_limit_override ? <span className="ml-1 text-[9px] text-[#cfb53b]" title="Limite personalizzato">*</span> : null}
                        </td>
                        <td className="px-3 py-2 text-[#94a3b8]">{u.games_count ?? 0}</td>
                        <td className="px-3 py-2">
                          <button
                            onClick={() => handleToggleAdmin(u.id, u.is_admin)}
                            className={`text-[10px] px-2 py-0.5 rounded ${u.is_admin ? 'bg-[#cfb53b]/20 text-[#cfb53b] hover:bg-red-400/20 hover:text-red-400' : 'bg-[#2d3748] text-[#94a3b8] hover:bg-[#cfb53b]/20 hover:text-[#cfb53b]'}`}
                            title={u.is_admin ? "Revoca Admin" : "Concedi Admin"}
                            data-testid={`toggle-admin-${u.id}`}
                          >
                            {u.is_admin ? <Crown size={10} className="inline mr-1" /> : null}
                            {u.is_admin ? "Admin" : "User"}
                          </button>
                        </td>
                        <td className="px-3 py-2">
                          <div className="flex gap-1 items-center">
                            <Button variant="ghost" size="sm" onClick={() => isEditing ? setEditingUserId(null) : openUserEdit(u)} className="h-6 text-[#94a3b8] hover:text-[#cfb53b] text-[10px] px-2" data-testid={`edit-user-${u.id}`} title="Modifica">
                              {isEditing ? <X size={12} /> : <Edit size={12} />}
                            </Button>
                            {!u.is_admin && (
                              confirmDelete === `user-${u.id}` ? (
                                <div className="flex gap-1">
                                  <Button variant="ghost" size="sm" onClick={() => handleDeleteUser(u.id)} className="h-6 text-red-400 hover:bg-red-400/10 text-[10px] px-2">Sì</Button>
                                  <Button variant="ghost" size="sm" onClick={() => setConfirmDelete(null)} className="h-6 text-[#94a3b8] text-[10px] px-2">No</Button>
                                </div>
                              ) : (
                                <Button variant="ghost" size="sm" onClick={() => setConfirmDelete(`user-${u.id}`)} className="h-6 text-[#8b0000] hover:text-red-400 text-[10px]" title="Elimina">
                                  <Trash2 size={12} />
                                </Button>
                              )
                            )}
                          </div>
                        </td>
                      </tr>
                      {isEditing && (
                        <tr className="border-t border-[#cfb53b]/20 bg-[#0a0a0c]/70">
                          <td colSpan={8} className="px-4 py-4">
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                              {/* Limits */}
                              <div className="bg-[#1a1a1d] border border-[#2d3748] rounded p-3">
                                <h4 className="text-[11px] font-heading text-[#cfb53b] mb-2 flex items-center gap-1.5"><Sparkles size={11} /> Generazioni AI</h4>
                                <div className="space-y-2">
                                  <div>
                                    <Label className="text-[10px] text-[#94a3b8]">Usate oggi</Label>
                                    <Input type="number" value={userEditForm.daily_pages} onChange={e => setUserEditForm({...userEditForm, daily_pages: e.target.value})} className="h-7 bg-[#0a0a0c] border-[#2d3748] text-[#e2e8f0] text-xs" data-testid={`user-edit-pages-${u.id}`} />
                                  </div>
                                  <div>
                                    <Label className="text-[10px] text-[#94a3b8]">Limite personalizzato (0 = globale)</Label>
                                    <Input type="number" value={userEditForm.daily_limit_override} onChange={e => setUserEditForm({...userEditForm, daily_limit_override: e.target.value})} placeholder={`Globale: ${config?.daily_free_pages || 10}`} className="h-7 bg-[#0a0a0c] border-[#2d3748] text-[#e2e8f0] text-xs" data-testid={`user-edit-limit-${u.id}`} />
                                  </div>
                                  <div className="flex gap-1.5 pt-1">
                                    <Button size="sm" onClick={() => handleSaveUserLimits(u.id)} className="h-7 flex-1 bg-[#cfb53b] hover:bg-[#d4af37] text-[#0a0a0c] text-[10px]" data-testid={`save-limits-${u.id}`}>Salva</Button>
                                    <Button size="sm" variant="ghost" onClick={() => handleResetToday(u.id)} className="h-7 text-[10px] text-[#94a3b8] hover:text-[#cfb53b]" title="Azzera generazioni odierne">Reset</Button>
                                  </div>
                                </div>
                              </div>

                              {/* Password */}
                              <div className="bg-[#1a1a1d] border border-[#2d3748] rounded p-3">
                                <h4 className="text-[11px] font-heading text-[#cfb53b] mb-2 flex items-center gap-1.5"><Key size={11} /> Reset Password</h4>
                                <div className="space-y-2">
                                  <Input type="text" placeholder="Nuova password" value={userEditForm.new_password} onChange={e => setUserEditForm({...userEditForm, new_password: e.target.value})} className="h-7 bg-[#0a0a0c] border-[#2d3748] text-[#e2e8f0] text-xs" data-testid={`user-edit-pwd-${u.id}`} />
                                  <Button size="sm" onClick={() => handleResetPassword(u.id)} className="h-7 w-full bg-[#cfb53b] hover:bg-[#d4af37] text-[#0a0a0c] text-[10px]" data-testid={`save-pwd-${u.id}`}>Reimposta</Button>
                                  <p className="text-[9px] text-[#64748b]">Min 4 caratteri. L'utente dovrà usare la nuova.</p>
                                </div>
                              </div>

                              {/* Games drilldown */}
                              <div className="bg-[#1a1a1d] border border-[#2d3748] rounded p-3">
                                <h4 className="text-[11px] font-heading text-[#cfb53b] mb-2 flex items-center gap-1.5"><Gamepad2 size={11} /> Personaggi ({userGames[u.id]?.length ?? 0})</h4>
                                <div className="space-y-1.5 max-h-32 overflow-y-auto">
                                  {(userGames[u.id] || []).length === 0 ? (
                                    <p className="text-[10px] text-[#64748b]">Nessun personaggio</p>
                                  ) : (userGames[u.id] || []).map(g => (
                                    <div key={g.id} className="flex items-center justify-between border-b border-[#2d3748] pb-1">
                                      <span className="text-[10px] text-[#e2e8f0]">{g.character?.name} <span className="text-[#64748b]">Lv.{g.character?.level} {g.character?.class_type}</span></span>
                                      <Button size="sm" variant="ghost" onClick={() => openCharEdit(g)} className="h-5 text-[10px] text-[#cfb53b] hover:text-[#d4af37] px-1.5" data-testid={`edit-char-${g.id}`}>
                                        <Edit size={10} />
                                      </Button>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                      </React.Fragment>
                    );})}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>

          {/* Games */}
          <TabsContent value="games">
            <div className="bg-[#1a1a1d]/80 border border-[#2d3748] rounded-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="bg-[#0a0a0c] text-[#94a3b8]">
                    <tr>
                      <th className="px-3 py-2 text-left font-heading">Character</th>
                      <th className="px-3 py-2 text-left font-heading">Class</th>
                      <th className="px-3 py-2 text-left font-heading">Level</th>
                      <th className="px-3 py-2 text-left font-heading">Gold</th>
                      <th className="px-3 py-2 text-left font-heading">Chapter</th>
                      <th className="px-3 py-2 text-left font-heading">Setting</th>
                      <th className="px-3 py-2 text-left font-heading">Created</th>
                      <th className="px-3 py-2 text-left font-heading">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {games.map(g => (
                      <tr key={g.id} className="border-t border-[#2d3748] hover:bg-[#0a0a0c]/50">
                        <td className="px-3 py-2 text-[#e2e8f0]">{g.character?.name}</td>
                        <td className="px-3 py-2 text-[#94a3b8] capitalize">{g.character?.class_type}</td>
                        <td className="px-3 py-2 text-[#cfb53b]">{g.character?.level}</td>
                        <td className="px-3 py-2"><Coins size={10} className="inline text-[#cfb53b] mr-0.5" />{g.character?.gold || 0}</td>
                        <td className="px-3 py-2">{g.current_chapter || 1}</td>
                        <td className="px-3 py-2 text-[#94a3b8]">{g.setting}</td>
                        <td className="px-3 py-2 text-[#64748b]">{g.created_at?.slice(0, 10)}</td>
                        <td className="px-3 py-2">
                          <div className="flex gap-1 items-center">
                            <Button variant="ghost" size="sm" onClick={() => openCharEdit(g)} className="h-6 text-[#94a3b8] hover:text-[#cfb53b] text-[10px]" title="Modifica personaggio" data-testid={`edit-char-row-${g.id}`}>
                              <Edit size={12} />
                            </Button>
                            {confirmDelete === `game-${g.id}` ? (
                              <div className="flex gap-1">
                                <Button variant="ghost" size="sm" onClick={() => handleDeleteGame(g.id)} className="h-6 text-red-400 hover:bg-red-400/10 text-[10px] px-2">Sì</Button>
                                <Button variant="ghost" size="sm" onClick={() => setConfirmDelete(null)} className="h-6 text-[#94a3b8] text-[10px] px-2">No</Button>
                              </div>
                            ) : (
                              <Button variant="ghost" size="sm" onClick={() => setConfirmDelete(`game-${g.id}`)} className="h-6 text-[#8b0000] hover:text-red-400 text-[10px]" title="Elimina">
                                <Trash2 size={12} />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>

          {/* Payments */}
          <TabsContent value="payments">
            <div className="bg-[#1a1a1d]/80 border border-[#2d3748] rounded-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="bg-[#0a0a0c] text-[#94a3b8]">
                    <tr>
                      <th className="px-3 py-2 text-left font-heading">Session</th>
                      <th className="px-3 py-2 text-left font-heading">User</th>
                      <th className="px-3 py-2 text-left font-heading">Pack</th>
                      <th className="px-3 py-2 text-left font-heading">Gold</th>
                      <th className="px-3 py-2 text-left font-heading">Amount</th>
                      <th className="px-3 py-2 text-left font-heading">Status</th>
                      <th className="px-3 py-2 text-left font-heading">Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {transactions.length === 0 ? (
                      <tr><td colSpan="7" className="px-3 py-8 text-center text-[#64748b]">No transactions yet</td></tr>
                    ) : transactions.map((tx) => (
                      <tr key={tx.session_id || tx.id || `${tx.user_id}-${tx.created_at}`} className="border-t border-[#2d3748] hover:bg-[#0a0a0c]/50">
                        <td className="px-3 py-2 text-[#64748b] font-mono">{tx.session_id?.slice(0, 12)}...</td>
                        <td className="px-3 py-2 text-[#94a3b8]">{tx.user_id?.slice(0, 8)}...</td>
                        <td className="px-3 py-2 text-[#e2e8f0]">{tx.pack_id}</td>
                        <td className="px-3 py-2 text-[#cfb53b]">{tx.gold}</td>
                        <td className="px-3 py-2">${tx.amount}</td>
                        <td className="px-3 py-2">
                          <span className={`px-1.5 py-0.5 rounded-full text-[10px] ${
                            tx.payment_status === 'paid' ? 'bg-green-500/20 text-green-400' :
                            tx.payment_status === 'initiated' ? 'bg-yellow-500/20 text-yellow-400' :
                            'bg-red-500/20 text-red-400'
                          }`}>{tx.payment_status}</span>
                        </td>
                        <td className="px-3 py-2 text-[#64748b]">{tx.created_at?.slice(0, 16)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>

          {/* Config */}
          <TabsContent value="config">
            <div className="space-y-6 max-w-lg">
              <div className="bg-[#1a1a1d]/80 border border-[#2d3748] rounded-lg p-5">
                <h3 className="font-heading text-sm text-[#cfb53b] mb-4">Daily Free Pages Limit</h3>
                <div className="flex gap-3">
                  <Input
                    type="number"
                    value={dailyPages}
                    onChange={e => setDailyPages(e.target.value)}
                    className="bg-[#0a0a0c] border-[#2d3748] text-[#e2e8f0] w-32"
                    data-testid="config-daily-pages"
                  />
                  <Button onClick={handleUpdateConfig} className="bg-[#cfb53b] hover:bg-[#d4af37] text-[#0a0a0c] font-heading text-xs">
                    Save
                  </Button>
                </div>
                <p className="text-[10px] text-[#64748b] mt-2">Current: {config?.daily_free_pages} pages/day for free users</p>
              </div>

              <div className="bg-[#1a1a1d]/80 border border-[#2d3748] rounded-lg p-5">
                <h3 className="font-heading text-sm text-[#cfb53b] mb-4">Add Gold to User</h3>
                <div className="space-y-3">
                  <Input
                    placeholder="User ID"
                    value={addGoldUserId}
                    onChange={e => setAddGoldUserId(e.target.value)}
                    className="bg-[#0a0a0c] border-[#2d3748] text-[#e2e8f0]"
                  />
                  <div className="flex gap-3">
                    <Input
                      type="number"
                      placeholder="Gold amount"
                      value={addGoldAmount}
                      onChange={e => setAddGoldAmount(e.target.value)}
                      className="bg-[#0a0a0c] border-[#2d3748] text-[#e2e8f0]"
                    />
                    <Button onClick={handleAddGold} className="bg-[#cfb53b] hover:bg-[#d4af37] text-[#0a0a0c] font-heading text-xs">
                      <Coins size={14} className="mr-1" /> Add
                    </Button>
                  </div>
                </div>
              </div>

              <div className="bg-[#1a1a1d]/80 border border-[#2d3748] rounded-lg p-5">
                <h3 className="font-heading text-sm text-[#cfb53b] mb-4">Gold Packs (Stripe)</h3>
                <div className="space-y-2">
                  {config?.gold_packs && Object.entries(config.gold_packs).map(([id, pack]) => (
                    <div key={id} className="flex justify-between text-xs border-b border-[#2d3748] pb-2">
                      <span className="text-[#e2e8f0]">{pack.name}</span>
                      <span className="text-[#cfb53b]">{pack.gold}g = ${pack.price}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Shop Editor */}
          <TabsContent value="shop-editor">
            {/* === All Shop Items table (built-in + custom) === */}
            <div className="bg-[#1a1a1d]/80 border border-[#2d3748] rounded-lg p-4 sm:p-5 mb-6">
              <h3 className="font-heading text-sm text-[#cfb53b] mb-3 flex items-center gap-2">
                <ShoppingBag size={14} /> All Shop Items ({allShopItems.length})
              </h3>
              <p className="text-[11px] text-[#94a3b8] mb-3">
                Edit any item — built-in items are stored as overrides (reset reverts to defaults). Custom items are edited in place.
              </p>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-left text-[#94a3b8] border-b border-[#2d3748]">
                      <th className="py-2 pr-2">Name / ID</th>
                      <th className="py-2 px-2">Category</th>
                      <th className="py-2 px-2">Price</th>
                      <th className="py-2 px-2">Type</th>
                      <th className="py-2 px-2">Source</th>
                      <th className="py-2 pl-2 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {allShopItems.map((item) => (
                      <tr
                        key={item.id}
                        className={`border-b border-[#2d3748]/40 hover:bg-[#0a0a0c]/50 ${item.disabled ? "opacity-50" : ""}`}
                        data-testid={`admin-shop-row-${item.id}`}
                      >
                        <td className="py-2 pr-2">
                          <div className="text-[#e2e8f0] font-medium">{item.name}</div>
                          <div className="text-[10px] text-[#64748b]">{item.id}</div>
                        </td>
                        <td className="py-2 px-2 text-[#cbd5e1]">{item.category}</td>
                        <td className="py-2 px-2 text-[#fbbf24]">{item.price}g</td>
                        <td className="py-2 px-2 text-[#cbd5e1]">{item.type}</td>
                        <td className="py-2 px-2">
                          {item.is_builtin ? (
                            <span className={`text-[10px] px-1.5 py-0.5 rounded ${item.has_override ? "bg-[#fbbf24]/20 text-[#fbbf24]" : "bg-[#2d3748] text-[#94a3b8]"}`}>
                              built-in{item.has_override ? " *" : ""}
                            </span>
                          ) : (
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#a78bfa]/20 text-[#a78bfa]">custom</span>
                          )}
                          {item.disabled && (
                            <span className="ml-1 text-[10px] px-1.5 py-0.5 rounded bg-red-500/20 text-red-400">disabled</span>
                          )}
                        </td>
                        <td className="py-2 pl-2 text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              size="sm"
                              variant="ghost"
                              data-testid={`edit-shop-item-${item.id}`}
                              onClick={() => setEditingItem({
                                id: item.id,
                                is_builtin: item.is_builtin,
                                draft: {
                                  name: item.name,
                                  price: item.price,
                                  description: item.description,
                                  category: item.category,
                                  type: item.type,
                                  attack: item.attack,
                                  defense: item.defense,
                                  hp: item.hp,
                                  max_hp: item.max_hp,
                                  mana_cost: item.mana_cost,
                                  effect: item.effect,
                                  effect_value: item.effect_value,
                                  equippable: item.equippable,
                                  slot: item.slot,
                                },
                              })}
                              className="h-7 px-2 text-[#60a5fa] hover:bg-[#60a5fa]/10"
                            >
                              <Edit size={11} />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              data-testid={`toggle-shop-item-${item.id}`}
                              onClick={() => handleToggleShopItemDisabled(item.id, item.disabled)}
                              className="h-7 px-2 text-[#94a3b8] hover:bg-[#cfb53b]/10 text-[10px]"
                              title={item.disabled ? "Enable" : "Disable"}
                            >
                              {item.disabled ? "On" : "Off"}
                            </Button>
                            {item.is_builtin && item.has_override && (
                              <Button
                                size="sm"
                                variant="ghost"
                                data-testid={`reset-shop-item-${item.id}`}
                                onClick={() => handleResetShopItem(item.id)}
                                className="h-7 px-2 text-[#f87171] hover:bg-red-500/10"
                                title="Reset to default"
                              >
                                <RefreshCw size={11} />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Edit modal */}
            {editingItem && (
              <div
                className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
                onClick={() => setEditingItem(null)}
                data-testid="edit-shop-item-modal"
              >
                <div
                  className="bg-[#1a1a1d] border-2 border-[#cfb53b]/40 rounded-xl p-5 w-full max-w-lg max-h-[90vh] overflow-y-auto"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-heading text-base text-[#cfb53b] flex items-center gap-2">
                      <Edit size={14} /> Edit: {editingItem.draft.name}
                    </h3>
                    <button onClick={() => setEditingItem(null)} className="text-[#94a3b8] hover:text-[#cfb53b]">
                      <X size={16} />
                    </button>
                  </div>
                  <div className="space-y-3">
                    <div>
                      <Label className="text-xs text-[#94a3b8]">Name</Label>
                      <Input value={editingItem.draft.name || ""} onChange={e => setEditingItem({ ...editingItem, draft: { ...editingItem.draft, name: e.target.value } })} className="bg-[#0a0a0c] border-[#2d3748] text-[#e2e8f0]" />
                    </div>
                    <div>
                      <Label className="text-xs text-[#94a3b8]">Description</Label>
                      <Input value={editingItem.draft.description || ""} onChange={e => setEditingItem({ ...editingItem, draft: { ...editingItem.draft, description: e.target.value } })} className="bg-[#0a0a0c] border-[#2d3748] text-[#e2e8f0]" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs text-[#94a3b8]">Price (gold)</Label>
                        <Input type="number" value={editingItem.draft.price ?? 0} onChange={e => setEditingItem({ ...editingItem, draft: { ...editingItem.draft, price: parseInt(e.target.value) || 0 } })} className="bg-[#0a0a0c] border-[#2d3748] text-[#e2e8f0]" />
                      </div>
                      <div>
                        <Label className="text-xs text-[#94a3b8]">Category</Label>
                        <Input value={editingItem.draft.category || ""} onChange={e => setEditingItem({ ...editingItem, draft: { ...editingItem.draft, category: e.target.value } })} className="bg-[#0a0a0c] border-[#2d3748] text-[#e2e8f0]" />
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <Label className="text-xs text-[#94a3b8]">Attack</Label>
                        <Input type="number" value={editingItem.draft.attack ?? 0} onChange={e => setEditingItem({ ...editingItem, draft: { ...editingItem.draft, attack: parseInt(e.target.value) || 0 } })} className="bg-[#0a0a0c] border-[#2d3748] text-[#e2e8f0]" />
                      </div>
                      <div>
                        <Label className="text-xs text-[#94a3b8]">Defense</Label>
                        <Input type="number" value={editingItem.draft.defense ?? 0} onChange={e => setEditingItem({ ...editingItem, draft: { ...editingItem.draft, defense: parseInt(e.target.value) || 0 } })} className="bg-[#0a0a0c] border-[#2d3748] text-[#e2e8f0]" />
                      </div>
                      <div>
                        <Label className="text-xs text-[#94a3b8]">HP</Label>
                        <Input type="number" value={editingItem.draft.hp ?? 0} onChange={e => setEditingItem({ ...editingItem, draft: { ...editingItem.draft, hp: parseInt(e.target.value) || 0 } })} className="bg-[#0a0a0c] border-[#2d3748] text-[#e2e8f0]" />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs text-[#94a3b8]">Effect</Label>
                        <Input value={editingItem.draft.effect || ""} onChange={e => setEditingItem({ ...editingItem, draft: { ...editingItem.draft, effect: e.target.value } })} className="bg-[#0a0a0c] border-[#2d3748] text-[#e2e8f0]" />
                      </div>
                      <div>
                        <Label className="text-xs text-[#94a3b8]">Effect Value</Label>
                        <Input type="number" value={editingItem.draft.effect_value ?? 0} onChange={e => setEditingItem({ ...editingItem, draft: { ...editingItem.draft, effect_value: parseInt(e.target.value) || 0 } })} className="bg-[#0a0a0c] border-[#2d3748] text-[#e2e8f0]" />
                      </div>
                    </div>
                    <div className="flex gap-2 pt-2">
                      <Button
                        onClick={() => handlePatchShopItem(editingItem.id, editingItem.draft)}
                        data-testid="save-shop-item"
                        className="flex-1 bg-[#cfb53b] hover:bg-[#d4af37] text-[#0a0a0c] font-heading"
                      >
                        Save
                      </Button>
                      <Button onClick={() => setEditingItem(null)} variant="outline" className="border-[#2d3748] text-[#94a3b8]">
                        Cancel
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Create Form */}
              <div className="bg-[#1a1a1d]/80 border border-[#2d3748] rounded-lg p-5">
                <h3 className="font-heading text-sm text-[#cfb53b] mb-4 flex items-center gap-2"><Plus size={14} /> Create Shop Item</h3>
                <div className="space-y-3">
                  <div>
                    <Label className="text-xs text-[#94a3b8]">Name</Label>
                    <Input value={newItem.name} onChange={e => setNewItem({...newItem, name: e.target.value})} className="bg-[#0a0a0c] border-[#2d3748] text-[#e2e8f0]" placeholder="Sword of Light" />
                  </div>
                  <div>
                    <Label className="text-xs text-[#94a3b8]">Description</Label>
                    <Input value={newItem.description} onChange={e => setNewItem({...newItem, description: e.target.value})} className="bg-[#0a0a0c] border-[#2d3748] text-[#e2e8f0]" placeholder="A powerful sword..." />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs text-[#94a3b8]">Category</Label>
                      <Select value={newItem.category} onValueChange={v => setNewItem({...newItem, category: v})}>
                        <SelectTrigger className="bg-[#0a0a0c] border-[#2d3748] text-[#e2e8f0]"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="potions">Potions</SelectItem>
                          <SelectItem value="equipment">Equipment</SelectItem>
                          <SelectItem value="allies">Allies</SelectItem>
                          <SelectItem value="abilities">Abilities</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs text-[#94a3b8]">Type</Label>
                      <Select value={newItem.item_type} onValueChange={v => setNewItem({...newItem, item_type: v, equippable: v === "object", slot: v === "object" ? "weapon" : "none"})}>
                        <SelectTrigger className="bg-[#0a0a0c] border-[#2d3748] text-[#e2e8f0]"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="ability">Ability/Potion</SelectItem>
                          <SelectItem value="object">Equipment</SelectItem>
                          <SelectItem value="ally">Ally</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <Label className="text-xs text-[#94a3b8]">Price (gold)</Label>
                      <Input type="number" value={newItem.price} onChange={e => setNewItem({...newItem, price: parseInt(e.target.value)||0})} className="bg-[#0a0a0c] border-[#2d3748] text-[#e2e8f0]" />
                    </div>
                    <div>
                      <Label className="text-xs text-[#94a3b8]">ATK</Label>
                      <Input type="number" value={newItem.attack} onChange={e => setNewItem({...newItem, attack: parseInt(e.target.value)||0})} className="bg-[#0a0a0c] border-[#2d3748] text-[#e2e8f0]" />
                    </div>
                    <div>
                      <Label className="text-xs text-[#94a3b8]">DEF</Label>
                      <Input type="number" value={newItem.defense} onChange={e => setNewItem({...newItem, defense: parseInt(e.target.value)||0})} className="bg-[#0a0a0c] border-[#2d3748] text-[#e2e8f0]" />
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <Label className="text-xs text-[#94a3b8]">HP</Label>
                      <Input type="number" value={newItem.hp} onChange={e => { const v = parseInt(e.target.value)||0; setNewItem({...newItem, hp: v, max_hp: v}); }} className="bg-[#0a0a0c] border-[#2d3748] text-[#e2e8f0]" />
                    </div>
                    <div>
                      <Label className="text-xs text-[#94a3b8]">Mana Cost</Label>
                      <Input type="number" value={newItem.mana_cost} onChange={e => setNewItem({...newItem, mana_cost: parseInt(e.target.value)||0})} className="bg-[#0a0a0c] border-[#2d3748] text-[#e2e8f0]" />
                    </div>
                    <div>
                      <Label className="text-xs text-[#94a3b8]">Effect Value</Label>
                      <Input type="number" value={newItem.effect_value} onChange={e => setNewItem({...newItem, effect_value: parseInt(e.target.value)||0})} className="bg-[#0a0a0c] border-[#2d3748] text-[#e2e8f0]" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs text-[#94a3b8]">Effect</Label>
                      <Select value={newItem.effect} onValueChange={v => setNewItem({...newItem, effect: v})}>
                        <SelectTrigger className="bg-[#0a0a0c] border-[#2d3748] text-[#e2e8f0]"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">None</SelectItem>
                          <SelectItem value="damage">Damage</SelectItem>
                          <SelectItem value="heal">Heal</SelectItem>
                          <SelectItem value="defense">Defense</SelectItem>
                          <SelectItem value="buff">Buff</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    {newItem.item_type === "object" && (
                      <div>
                        <Label className="text-xs text-[#94a3b8]">Slot</Label>
                        <Select value={newItem.slot} onValueChange={v => setNewItem({...newItem, slot: v})}>
                          <SelectTrigger className="bg-[#0a0a0c] border-[#2d3748] text-[#e2e8f0]"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="weapon">Weapon</SelectItem>
                            <SelectItem value="armor">Armor</SelectItem>
                            <SelectItem value="accessory">Accessory</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                  </div>

                  {/* Rarity + Foil */}
                  <div className="bg-purple-400/5 border border-purple-400/20 rounded-lg p-3 mt-3">
                    <p className="text-[11px] font-heading text-purple-300 mb-2 flex items-center gap-1.5">
                      <Sparkles size={11} /> Rarità e Brillantezza
                    </p>
                    <div className="grid grid-cols-[1fr_auto] gap-3 items-end">
                      <div>
                        <Label className="text-[10px] text-[#94a3b8]">Rarità</Label>
                        <Select value={newItem.rarity || "common"} onValueChange={v => setNewItem({...newItem, rarity: v})}>
                          <SelectTrigger className="bg-[#0a0a0c] border-[#2d3748] text-[#e2e8f0] h-8" data-testid="new-item-rarity">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="common">Comune</SelectItem>
                            <SelectItem value="uncommon">Non Comune</SelectItem>
                            <SelectItem value="rare">Raro</SelectItem>
                            <SelectItem value="epic">Epico</SelectItem>
                            <SelectItem value="legendary">Leggendario</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <label className="flex items-center gap-2 bg-[#0a0a0c] border border-[#2d3748] rounded px-3 h-8 cursor-pointer hover:border-purple-400/40" data-testid="new-item-foil-label">
                        <input
                          type="checkbox"
                          checked={!!newItem.foil}
                          onChange={e => setNewItem({...newItem, foil: e.target.checked})}
                          className="accent-[#cfb53b]"
                          data-testid="new-item-foil"
                        />
                        <span className="text-xs text-[#e2e8f0] font-heading">FOIL ✨</span>
                      </label>
                    </div>
                    <p className="text-[9px] text-[#64748b] mt-2">Le carte FOIL hanno un effetto olografico animato in gioco.</p>
                  </div>

                  {/* Seasonal / Limited Time availability */}
                  <div className="bg-orange-400/5 border border-orange-400/20 rounded-lg p-3 mt-3">
                    <p className="text-[11px] font-heading text-orange-400 mb-2 flex items-center gap-1.5">
                      <Hourglass size={11} /> Disponibilità stagionale (opzionale)
                    </p>
                    <p className="text-[9px] text-[#64748b] mb-2">Lascia vuoto per rendere l'oggetto sempre disponibile.</p>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label className="text-[10px] text-[#94a3b8]">Disponibile dal</Label>
                        <Input type="datetime-local" value={newItem.available_from} onChange={e => setNewItem({...newItem, available_from: e.target.value})} className="bg-[#0a0a0c] border-[#2d3748] text-[#e2e8f0] text-xs h-8" data-testid="new-item-available-from" />
                      </div>
                      <div>
                        <Label className="text-[10px] text-[#94a3b8]">Disponibile fino al</Label>
                        <Input type="datetime-local" value={newItem.available_until} onChange={e => setNewItem({...newItem, available_until: e.target.value})} className="bg-[#0a0a0c] border-[#2d3748] text-[#e2e8f0] text-xs h-8" data-testid="new-item-available-until" />
                      </div>
                    </div>
                  </div>

                  <Button onClick={handleCreateShopItem} className="w-full bg-[#cfb53b] hover:bg-[#d4af37] text-[#0a0a0c] font-heading">
                    <Plus size={14} className="mr-1.5" /> Create Item
                  </Button>
                </div>
              </div>

              {/* Custom Items List */}
              <div className="bg-[#1a1a1d]/80 border border-[#2d3748] rounded-lg p-5">
                <h3 className="font-heading text-sm text-[#cfb53b] mb-4 flex items-center gap-2"><ShoppingBag size={14} /> Custom Items ({customItems.length})</h3>
                {customItems.length === 0 ? (
                  <p className="text-xs text-[#64748b] text-center py-8">No custom items yet. Create one!</p>
                ) : (
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {customItems.map(item => (
                      <div key={item.id} className="bg-[#0a0a0c] border border-[#2d3748] rounded-lg p-3">
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 mb-1 flex-wrap">
                              <span className="font-heading text-xs text-[#e2e8f0]">{item.name}</span>
                              <span className="text-[9px] bg-[#cfb53b]/15 text-[#cfb53b] px-1.5 py-0.5 rounded">{item.category}</span>
                              <span className="text-[9px] text-[#cfb53b]"><Coins size={8} className="inline mr-0.5" />{item.price}</span>
                              {item.rarity && item.rarity !== "common" && (
                                <span className="text-[9px] px-1.5 py-0.5 rounded font-heading uppercase" style={{
                                  color: item.rarity === "legendary" ? "#f59e0b" : item.rarity === "epic" ? "#a855f7" : item.rarity === "rare" ? "#3b82f6" : "#22c55e",
                                  background: item.rarity === "legendary" ? "rgba(245,158,11,0.15)" : item.rarity === "epic" ? "rgba(168,85,247,0.15)" : item.rarity === "rare" ? "rgba(59,130,246,0.15)" : "rgba(34,197,94,0.15)"
                                }}>{item.rarity}</span>
                              )}
                              {item.foil && (
                                <span className="text-[9px] bg-[#fef08a]/15 text-[#fef08a] px-1.5 py-0.5 rounded flex items-center gap-1">
                                  <Sparkles size={8} /> FOIL
                                </span>
                              )}
                              {(item.available_from || item.available_until) && (
                                <span className="text-[9px] bg-orange-400/15 text-orange-400 px-1.5 py-0.5 rounded flex items-center gap-1">
                                  <Hourglass size={8} /> Limitato
                                </span>
                              )}
                            </div>
                            <p className="text-[10px] text-[#94a3b8] mb-1">{item.description}</p>
                            <div className="flex gap-2 text-[9px] flex-wrap">
                              {item.attack > 0 && <span className="text-red-400">ATK +{item.attack}</span>}
                              {item.defense > 0 && <span className="text-blue-400">DEF +{item.defense}</span>}
                              {item.hp > 0 && <span className="text-green-400">HP {item.hp}</span>}
                              {item.effect !== "none" && <span className="text-purple-400">{item.effect} {item.effect_value}</span>}
                            </div>
                            {(item.available_from || item.available_until) && (
                              <div className="text-[9px] text-orange-300 mt-1 font-mono">
                                {item.available_from && <>Da: {item.available_from.slice(0, 16)}</>}
                                {item.available_from && item.available_until && ' · '}
                                {item.available_until && <>A: {item.available_until.slice(0, 16)}</>}
                              </div>
                            )}
                          </div>
                          <div className="shrink-0 flex gap-1">
                            <Button variant="ghost" size="sm"
                              onClick={() => {
                                if (editingAvailability === item.id) {
                                  setEditingAvailability(null);
                                } else {
                                  setEditingAvailability(item.id);
                                  setAvailabilityForm({
                                    available_from: item.available_from ? item.available_from.slice(0, 16) : "",
                                    available_until: item.available_until ? item.available_until.slice(0, 16) : "",
                                  });
                                }
                              }}
                              className="h-6 text-[#94a3b8] hover:text-orange-400 text-[10px]"
                              title="Modifica disponibilità"
                              data-testid={`edit-availability-${item.id}`}
                            >
                              <Hourglass size={11} />
                            </Button>
                            {confirmDelete === `item-${item.id}` ? (
                              <div className="flex gap-1">
                                <Button variant="ghost" size="sm" onClick={() => handleDeleteCustomItem(item.id)} className="h-6 text-red-400 hover:bg-red-400/10 text-[10px] px-2">Yes</Button>
                                <Button variant="ghost" size="sm" onClick={() => setConfirmDelete(null)} className="h-6 text-[#94a3b8] text-[10px] px-2">No</Button>
                              </div>
                            ) : (
                              <Button variant="ghost" size="sm" onClick={() => setConfirmDelete(`item-${item.id}`)} className="h-6 text-[#8b0000] hover:text-red-400">
                                <Trash2 size={12} />
                              </Button>
                            )}
                          </div>
                        </div>
                        {editingAvailability === item.id && (
                          <div className="mt-3 pt-3 border-t border-orange-400/20 bg-orange-400/5 -mx-3 -mb-3 px-3 pb-3 rounded-b-lg">
                            <p className="text-[10px] font-heading text-orange-400 mb-2">Modifica finestra di disponibilità</p>
                            <div className="grid grid-cols-2 gap-2 mb-2">
                              <div>
                                <Label className="text-[9px] text-[#94a3b8]">Dal</Label>
                                <Input type="datetime-local" value={availabilityForm.available_from} onChange={e => setAvailabilityForm({...availabilityForm, available_from: e.target.value})} className="h-7 bg-[#0a0a0c] border-[#2d3748] text-[#e2e8f0] text-xs" data-testid={`avail-from-${item.id}`} />
                              </div>
                              <div>
                                <Label className="text-[9px] text-[#94a3b8]">Fino al</Label>
                                <Input type="datetime-local" value={availabilityForm.available_until} onChange={e => setAvailabilityForm({...availabilityForm, available_until: e.target.value})} className="h-7 bg-[#0a0a0c] border-[#2d3748] text-[#e2e8f0] text-xs" data-testid={`avail-until-${item.id}`} />
                              </div>
                            </div>
                            <div className="flex gap-1.5">
                              <Button size="sm" onClick={() => { handleUpdateItemAvailability(item.id, availabilityForm.available_from, availabilityForm.available_until); setEditingAvailability(null); }} className="h-7 flex-1 bg-orange-400 hover:bg-orange-500 text-[#0a0a0c] text-[10px]" data-testid={`save-avail-${item.id}`}>Salva</Button>
                              <Button size="sm" variant="ghost" onClick={() => { handleUpdateItemAvailability(item.id, "", ""); setEditingAvailability(null); }} className="h-7 text-[10px] text-[#94a3b8] hover:text-orange-400">Rimuovi</Button>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </TabsContent>

          {/* Classes Editor */}
          <TabsContent value="classes">
            {classData && (
              <div className="space-y-6">
                {/* Evolution Levels */}
                <div className="bg-[#1a1a1d]/80 border border-purple-500/20 rounded-lg p-5">
                  <h3 className="font-heading text-sm text-purple-300 mb-3">Evolution Levels</h3>
                  <p className="text-[10px] text-[#94a3b8] mb-2">Comma-separated levels at which class evolution becomes available (e.g. 5, 10)</p>
                  <div className="flex gap-3">
                    <Input
                      value={evoLevels}
                      onChange={e => setEvoLevels(e.target.value)}
                      placeholder="5, 10"
                      className="bg-[#0a0a0c] border-[#2d3748] text-[#e2e8f0] w-48"
                      data-testid="evo-levels-input"
                    />
                    <Button onClick={handleSaveEvoLevels} className="bg-purple-500/20 hover:bg-purple-500/30 text-purple-300 border border-purple-500/30 font-heading text-xs">
                      Save
                    </Button>
                  </div>
                  <p className="text-[10px] text-[#64748b] mt-2">Current: Lv. {classData.evolution_levels?.join(" & Lv. ")}</p>
                </div>
                {/* Base Class Stats */}
                <div className="bg-[#1a1a1d]/80 border border-[#2d3748] rounded-lg p-5">
                  <h3 className="font-heading text-sm text-[#cfb53b] mb-4">Base Class Stats</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {Object.entries(classData.stats).map(([className, stats]) => (
                      <div key={className} className="bg-[#0a0a0c] border border-[#2d3748] rounded-lg p-3">
                        <h4 className="font-heading text-xs text-[#cfb53b] capitalize mb-2">{className}</h4>
                        {editingClass && editingClass._name === className ? (
                          <div className="space-y-1.5">
                            {[["hp", "HP"], ["max_hp", "Max HP"], ["mana", "Mana"], ["max_mana", "Max Mana"], ["base_attack", "ATK"], ["base_defense", "DEF"]].map(([k, label]) => (
                              <div key={k} className="flex items-center justify-between gap-2">
                                <span className="text-[10px] text-[#94a3b8] w-16">{label}</span>
                                <Input type="number" value={editingClass[k]} onChange={e => setEditingClass({...editingClass, [k]: parseInt(e.target.value)||0})} className="bg-[#1a1a1d] border-[#2d3748] text-[#e2e8f0] h-6 text-xs w-20" />
                              </div>
                            ))}
                            <div className="flex gap-1 mt-2">
                              <Button size="sm" onClick={() => handleSaveClassStats(className)} className="flex-1 bg-[#cfb53b] text-[#0a0a0c] text-[10px] h-6">Save</Button>
                              <Button size="sm" variant="ghost" onClick={() => setEditingClass(null)} className="text-[#94a3b8] text-[10px] h-6">Cancel</Button>
                            </div>
                          </div>
                        ) : (
                          <>
                            <div className="grid grid-cols-3 gap-1 text-[10px] mb-2">
                              <span className="text-red-400">HP: {stats.hp}/{stats.max_hp}</span>
                              <span className="text-blue-300">Mana: {stats.mana}/{stats.max_mana}</span>
                              <span className="text-[#cfb53b]">ATK: {stats.base_attack}</span>
                              <span className="text-blue-400">DEF: {stats.base_defense}</span>
                            </div>
                            <Button size="sm" variant="ghost" onClick={() => setEditingClass({...stats, _name: className})} className="w-full text-[10px] text-[#cfb53b] hover:bg-[#cfb53b]/10 h-6">Edit</Button>
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Class Evolutions */}
                <div className="bg-[#1a1a1d]/80 border border-[#2d3748] rounded-lg p-5">
                  <h3 className="font-heading text-sm text-[#cfb53b] mb-4">Class Evolutions (Lv.{classData.evolution_levels?.join(" & Lv.")})</h3>
                  <div className="space-y-4">
                    {Object.entries(classData.evolutions).filter(([k]) => ["warrior","mage","rogue","ranger","necromancer"].includes(k)).map(([parentClass, evos]) => (
                      <div key={parentClass} className="bg-[#0a0a0c] border border-[#2d3748] rounded-lg p-3">
                        <h4 className="font-heading text-xs text-purple-300 capitalize mb-2">{parentClass}</h4>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                          {Object.entries(evos).filter(([k]) => k !== "i18n").map(([statKey, evo]) => (
                            <div key={statKey} className="border border-[#2d3748] rounded p-2">
                              {editingEvo && editingEvo._parent === parentClass && editingEvo._statKey === statKey ? (
                                <div className="space-y-1.5">
                                  <Input value={editingEvo.name} onChange={e => setEditingEvo({...editingEvo, name: e.target.value})} placeholder="Name" className="bg-[#1a1a1d] border-[#2d3748] text-[#e2e8f0] h-6 text-[10px]" />
                                  <Input value={editingEvo.description} onChange={e => setEditingEvo({...editingEvo, description: e.target.value})} placeholder="Description" className="bg-[#1a1a1d] border-[#2d3748] text-[#e2e8f0] h-6 text-[10px]" />
                                  <div className="grid grid-cols-2 gap-1">
                                    {[["bonus_max_hp", "HP"], ["bonus_base_attack", "ATK"], ["bonus_base_defense", "DEF"], ["bonus_max_mana", "Mana"]].map(([k, l]) => (
                                      <div key={k} className="flex items-center gap-1">
                                        <span className="text-[9px] text-[#94a3b8] w-8">{l}</span>
                                        <Input type="number" value={editingEvo[k]} onChange={e => setEditingEvo({...editingEvo, [k]: parseInt(e.target.value)||0})} className="bg-[#1a1a1d] border-[#2d3748] text-[#e2e8f0] h-5 text-[10px]" />
                                      </div>
                                    ))}
                                  </div>
                                  <div className="flex gap-1">
                                    <Button size="sm" onClick={() => handleSaveEvolution(parentClass, statKey)} className="flex-1 bg-purple-500/20 text-purple-300 text-[10px] h-5">Save</Button>
                                    <Button size="sm" variant="ghost" onClick={() => setEditingEvo(null)} className="text-[#94a3b8] text-[10px] h-5">X</Button>
                                  </div>
                                </div>
                              ) : (
                                <>
                                  <div className="flex justify-between items-center mb-1">
                                    <span className="font-heading text-[10px] text-[#e2e8f0]">{evo.name}</span>
                                    <span className="text-[8px] text-[#64748b] capitalize">{statKey}</span>
                                  </div>
                                  <p className="text-[9px] text-[#94a3b8] mb-1">{evo.desc}</p>
                                  <div className="flex flex-wrap gap-1 text-[8px] mb-1">
                                    {Object.entries(evo.bonus || {}).map(([k, v]) => (
                                      <span key={k} className="text-[#cfb53b]">+{v} {k.replace("max_","").replace("base_","")}</span>
                                    ))}
                                  </div>
                                  <Button size="sm" variant="ghost" onClick={() => setEditingEvo({
                                    _parent: parentClass, _statKey: statKey,
                                    name: evo.name, description: evo.desc,
                                    bonus_max_hp: evo.bonus?.max_hp || 0,
                                    bonus_base_attack: evo.bonus?.base_attack || 0,
                                    bonus_base_defense: evo.bonus?.base_defense || 0,
                                    bonus_max_mana: evo.bonus?.max_mana || 0,
                                  })} className="w-full text-[9px] text-purple-300 hover:bg-purple-500/10 h-5">Edit</Button>
                                </>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </TabsContent>

          {/* Audit Log */}
          <TabsContent value="audit">
            <div className="space-y-4">
              <div className="flex items-center gap-3 flex-wrap">
                <Label className="text-xs text-[#94a3b8]">Filtra azione:</Label>
                <Select value={auditFilterAction} onValueChange={(v) => { setAuditFilterAction(v); loadAuditLog(v); }}>
                  <SelectTrigger className="w-56 h-8 bg-[#0a0a0c] border-[#2d3748] text-[#e2e8f0] text-xs" data-testid="audit-filter-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tutte</SelectItem>
                    <SelectItem value="user_delete">Elimina utente</SelectItem>
                    <SelectItem value="user_role_update">Modifica ruolo admin</SelectItem>
                    <SelectItem value="user_limits_update">Modifica limiti AI</SelectItem>
                    <SelectItem value="user_password_reset">Reset password</SelectItem>
                    <SelectItem value="character_edit">Modifica personaggio</SelectItem>
                    <SelectItem value="game_delete">Elimina partita</SelectItem>
                    <SelectItem value="add_gold">Aggiungi oro</SelectItem>
                    <SelectItem value="shop_item_create">Crea oggetto shop</SelectItem>
                    <SelectItem value="shop_item_delete">Elimina oggetto shop</SelectItem>
                    <SelectItem value="config_update">Modifica config</SelectItem>
                    <SelectItem value="class_stats_update">Modifica stats classe</SelectItem>
                    <SelectItem value="class_evolution_update">Modifica evoluzione</SelectItem>
                    <SelectItem value="evolution_levels_update">Livelli evoluzione</SelectItem>
                  </SelectContent>
                </Select>
                <Button size="sm" variant="ghost" onClick={() => loadAuditLog(auditFilterAction)} className="h-8 text-xs text-[#94a3b8] hover:text-[#cfb53b]" data-testid="audit-refresh">
                  <RefreshCw size={12} className="mr-1" /> Ricarica
                </Button>
                <span className="text-[10px] text-[#64748b]">Totale: {auditTotal}</span>
              </div>
              <div className="bg-[#1a1a1d]/80 border border-[#2d3748] rounded-lg overflow-hidden">
                <div className="overflow-x-auto max-h-[600px]">
                  <table className="w-full text-xs" data-testid="audit-log-table">
                    <thead className="bg-[#0a0a0c] text-[#94a3b8] sticky top-0">
                      <tr>
                        <th className="px-3 py-2 text-left font-heading">Data</th>
                        <th className="px-3 py-2 text-left font-heading">Admin</th>
                        <th className="px-3 py-2 text-left font-heading">Azione</th>
                        <th className="px-3 py-2 text-left font-heading">Target</th>
                        <th className="px-3 py-2 text-left font-heading">Dettagli</th>
                      </tr>
                    </thead>
                    <tbody>
                      {auditEntries.length === 0 ? (
                        <tr><td colSpan={5} className="px-3 py-8 text-center text-[#64748b]">Nessun intervento registrato</td></tr>
                      ) : auditEntries.map(e => (
                        <tr key={e.id} className="border-t border-[#2d3748] hover:bg-[#0a0a0c]/50">
                          <td className="px-3 py-2 text-[#64748b] whitespace-nowrap font-mono text-[10px]">{e.created_at?.replace('T', ' ').slice(0, 19)}</td>
                          <td className="px-3 py-2 text-[#cfb53b]">{e.admin_username}</td>
                          <td className="px-3 py-2">
                            <span className="inline-block px-2 py-0.5 rounded-full text-[10px] bg-[#cfb53b]/10 text-[#cfb53b]">{e.action}</span>
                          </td>
                          <td className="px-3 py-2 text-[#94a3b8]">
                            <span className="text-[#64748b]">{e.target_type}:</span> <span className="font-mono text-[10px]">{e.target_id?.slice(0, 12)}{e.target_id?.length > 12 ? '...' : ''}</span>
                          </td>
                          <td className="px-3 py-2 text-[#94a3b8] font-mono text-[10px] max-w-md">
                            <div className="truncate" title={JSON.stringify(e.details)}>
                              {e.details && Object.keys(e.details).length > 0 ? JSON.stringify(e.details) : <span className="text-[#64748b]">—</span>}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* AI Usage */}
          <TabsContent value="ai-usage">
            <div className="bg-[#1a1a1d]/80 border border-[#cfb53b]/20 rounded-lg p-4 sm:p-6 space-y-4" data-testid="ai-usage-panel">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <h2 className="font-heading text-lg sm:text-xl text-[#cfb53b] flex items-center gap-2">
                    <Cpu size={18} /> Consumo Universal Key AI
                  </h2>
                  <p className="text-xs text-[#94a3b8] mt-1">
                    Contatore di chiamate AI effettuate e relativo costo per utente. Clicca su un utente per vedere il dettaglio delle operazioni.
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <select
                    value={aiUsageDays}
                    onChange={(e) => { const d = Number(e.target.value); setAiUsageDays(d); loadAiUsage(d); }}
                    className="bg-[#0a0a0c] border border-[#2d3748] rounded px-3 py-1.5 text-xs text-[#e2e8f0]"
                    data-testid="ai-usage-range"
                  >
                    <option value={1}>Ultime 24h</option>
                    <option value={7}>Ultimi 7 giorni</option>
                    <option value={30}>Ultimi 30 giorni</option>
                    <option value={90}>Ultimi 90 giorni</option>
                  </select>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => loadAiUsage(aiUsageDays)}
                    className="text-[#cfb53b] hover:bg-[#cfb53b]/10"
                    data-testid="ai-usage-refresh"
                  >
                    {loadingAiUsage ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
                  </Button>
                </div>
              </div>

              {loadingAiUsage ? (
                <div className="flex justify-center py-12"><Loader2 size={24} className="animate-spin text-[#cfb53b]" /></div>
              ) : aiUsage ? (
                <>
                  {/* Grand totals */}
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <div className="bg-[#0a0a0c] border border-[#2d3748] rounded-lg p-4 text-center" data-testid="stat-all-calls">
                      <p className="text-[10px] text-[#94a3b8] uppercase font-heading">Chiamate totali</p>
                      <p className="font-heading text-2xl text-[#e2e8f0]">{aiUsage.grand_totals.all_calls}</p>
                    </div>
                    <div className="bg-[#0a0a0c] border border-[#2d3748] rounded-lg p-4 text-center" data-testid="stat-successful">
                      <p className="text-[10px] text-[#94a3b8] uppercase font-heading">Riuscite</p>
                      <p className="font-heading text-2xl text-green-400">{aiUsage.grand_totals.successful}</p>
                    </div>
                    <div className="bg-[#0a0a0c] border border-[#2d3748] rounded-lg p-4 text-center" data-testid="stat-story-pages">
                      <p className="text-[10px] text-[#94a3b8] uppercase font-heading">Pagine trama</p>
                      <p className="font-heading text-2xl text-blue-400">{aiUsage.grand_totals.story_pages}</p>
                      <p className="text-[9px] text-[#64748b]">gemini-2.5-flash</p>
                    </div>
                    <div className="bg-[#0a0a0c] border border-[#2d3748] rounded-lg p-4 text-center" data-testid="stat-images">
                      <p className="text-[10px] text-[#94a3b8] uppercase font-heading">Immagini</p>
                      <p className="font-heading text-2xl text-purple-400">{aiUsage.grand_totals.images}</p>
                      <p className="text-[9px] text-[#64748b]">nano-banana + gpt-image-1</p>
                    </div>
                  </div>

                  {/* Grouped Table by User */}
                  <div>
                    <h3 className="font-heading text-sm text-[#cfb53b] mb-2">Consumo AI per Utente</h3>
                    <div className="bg-[#0a0a0c] border border-[#2d3748] rounded-lg overflow-x-auto">
                      <table className="w-full text-xs">
                        <thead className="bg-[#1a1a1d] border-b border-[#2d3748]">
                          <tr>
                            <th className="text-left p-2 font-heading text-[#94a3b8]">Utente</th>
                            <th className="text-right p-2 font-heading text-[#94a3b8]">Chiamate Totali</th>
                            <th className="text-right p-2 font-heading text-[#94a3b8]">Chiamate Testo</th>
                            <th className="text-right p-2 font-heading text-[#94a3b8]">Costo Testo ($)</th>
                            <th className="text-right p-2 font-heading text-[#94a3b8]">Chiamate Immagini</th>
                            <th className="text-right p-2 font-heading text-[#94a3b8]">Costo Immagini ($)</th>
                            <th className="text-right p-2 font-heading text-[#94a3b8]">Costo Totale ($)</th>
                          </tr>
                        </thead>
                        <tbody data-testid="ai-top-users-body">
                          {(!aiUsage.top_users || aiUsage.top_users.length === 0) ? (
                            <tr><td colSpan="7" className="text-center py-6 text-[#64748b]">Nessun consumo registrato.</td></tr>
                          ) : aiUsage.top_users.map((u) => {
                            const totalCost = ((u.text_cost || 0) + (u.image_cost || 0)).toFixed(5);
                            return (
                              <tr
                                key={u.user_id}
                                onClick={() => setSelectedAiUser(u)}
                                className="border-b border-[#2d3748]/50 hover:bg-[#cfb53b]/5 cursor-pointer transition-colors"
                              >
                                <td className="p-2 text-[#e2e8f0] truncate max-w-[160px]">{u.email}</td>
                                <td className="p-2 text-right font-heading text-[#cfb53b]">{u.total_calls}</td>
                                <td className="p-2 text-right text-blue-400">{u.text_calls}</td>
                                <td className="p-2 text-right text-blue-300 font-mono">${(u.text_cost || 0).toFixed(5)}</td>
                                <td className="p-2 text-right text-purple-400">{u.image_calls}</td>
                                <td className="p-2 text-right text-purple-300 font-mono">${(u.image_cost || 0).toFixed(5)}</td>
                                <td className="p-2 text-right font-heading text-[#cfb53b] font-mono">${totalCost}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  <div className="bg-[#1a1a1d]/60 border border-[#cfb53b]/15 rounded p-3 text-[11px] text-[#94a3b8]">
                    <p className="font-heading text-[#cfb53b] mb-1">Come calcolare i crediti consumati</p>
                    <p>Moltiplica i costi sopra per il costo unitario della Universal Key (chiedi il pricing a support@emergent.sh con i modelli e i token tipici usati dalla tua app). Esempio: se una pagina trama costa X crediti e una immagine Y crediti, il consumo totale ≈ <code className="text-[#cfb53b]">pagine × X + immagini × Y</code>.</p>
                  </div>
                </>
              ) : (
                <p className="text-center py-8 text-[#64748b]">Carica i dati…</p>
              )}
            </div>

            {/* Modal for User AI Usage Details */}
            {selectedAiUser && (
              <div
                className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
                onClick={() => setSelectedAiUser(null)}
                data-testid="ai-user-details-modal"
              >
                <div
                  className="bg-[#1a1a1d] border border-[#cfb53b]/30 rounded-lg p-5 w-full max-w-3xl max-h-[90vh] overflow-y-auto"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h3 className="font-heading text-sm text-[#cfb53b] flex items-center gap-2">
                        <Cpu size={14} /> Dettaglio Consumo AI
                      </h3>
                      <p className="text-xs text-[#94a3b8] mt-0.5 font-mono">{selectedAiUser.email}</p>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setSelectedAiUser(null)}
                      className="h-6 w-6 p-0 text-[#94a3b8] hover:text-[#cfb53b]"
                    >
                      <X size={14} />
                    </Button>
                  </div>

                  {/* Quick User Stats */}
                  <div className="grid grid-cols-3 gap-3 mb-4">
                    <div className="bg-[#0a0a0c] border border-[#2d3748] rounded p-2 text-center">
                      <p className="text-[9px] text-[#94a3b8] uppercase font-heading">Chiamate Totali</p>
                      <p className="font-heading text-lg text-[#e2e8f0]">{selectedAiUser.total_calls}</p>
                    </div>
                    <div className="bg-[#0a0a0c] border border-[#2d3748] rounded p-2 text-center">
                      <p className="text-[9px] text-[#94a3b8] uppercase font-heading font-mono text-blue-400">Testo / Immagini</p>
                      <p className="font-heading text-sm text-[#e2e8f0] mt-0.5">
                        <span className="text-blue-400">{selectedAiUser.text_calls}</span>
                        <span className="text-[#64748b] mx-1">/</span>
                        <span className="text-purple-400">{selectedAiUser.image_calls}</span>
                      </p>
                    </div>
                    <div className="bg-[#0a0a0c] border border-[#2d3748] rounded p-2 text-center">
                      <p className="text-[9px] text-[#94a3b8] uppercase font-heading text-[#cfb53b]">Costo Stimato</p>
                      <p className="font-heading text-lg text-[#cfb53b] font-mono">
                        ${((selectedAiUser.text_cost || 0) + (selectedAiUser.image_cost || 0)).toFixed(5)}
                      </p>
                    </div>
                  </div>

                  {/* Details table */}
                  <div className="bg-[#0a0a0c] border border-[#2d3748] rounded-lg overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead className="bg-[#1a1a1d] border-b border-[#2d3748]">
                        <tr>
                          <th className="text-left p-2 font-heading text-[#94a3b8]">Operazione</th>
                          <th className="text-left p-2 font-heading text-[#94a3b8]">Modello</th>
                          <th className="text-center p-2 font-heading text-[#94a3b8]">Esito</th>
                          <th className="text-right p-2 font-heading text-[#94a3b8]">Conteggio</th>
                          <th className="text-right p-2 font-heading text-[#94a3b8]">Costo ($)</th>
                        </tr>
                      </thead>
                      <tbody data-testid="ai-usage-table-body">
                        {(!selectedAiUser.details || selectedAiUser.details.length === 0) ? (
                          <tr><td colSpan="5" className="text-center py-6 text-[#64748b]">Nessuna operazione registrata.</td></tr>
                        ) : selectedAiUser.details.map((row, i) => (
                          <tr key={i} className="border-b border-[#2d3748]/50">
                            <td className="p-2 text-[#e2e8f0]">{row.operation || "—"}</td>
                            <td className="p-2 text-[#94a3b8] font-mono text-[11px]">{row.model}</td>
                            <td className="p-2 text-center">
                              {row.success ? (
                                <span className="text-green-400 text-[11px]">✓ ok</span>
                              ) : (
                                <span className="text-red-400 text-[11px]">✗ fail</span>
                              )}
                            </td>
                            <td className="p-2 text-right font-heading text-[#cfb53b]">{row.count}</td>
                            <td className="p-2 text-right font-mono text-[#94a3b8]">${(row.cost || 0).toFixed(5)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div className="flex justify-end mt-4">
                    <Button
                      onClick={() => setSelectedAiUser(null)}
                      className="bg-[#cfb53b] hover:bg-[#d4af37] text-[#0a0a0c] font-heading text-xs px-4"
                    >
                      Chiudi
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </TabsContent>

          {/* Audio Library — iter 90 */}
          <TabsContent value="audio">
            <AudioLibraryTab token={token} />
          </TabsContent>

          {/* Landing CMS — iter 92 */}
          <TabsContent value="landing">
            <LandingEditorTab token={token} />
          </TabsContent>

          {/* Drop Rates — iter 100 */}
          <TabsContent value="drop-rates">
            <DropRatesTab />
          </TabsContent>
        </Tabs>
      </div>

      {/* Character Edit Modal */}
      {editingCharGame && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4" onClick={() => setEditingCharGame(null)}>
          <div className="bg-[#1a1a1d] border border-[#cfb53b]/30 rounded-lg p-4 sm:p-5 max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="char-edit-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading text-sm text-[#cfb53b] flex items-center gap-2">
                <Edit size={14} /> Modifica Personaggio: {editingCharGame.character?.name}
              </h3>
              <Button variant="ghost" size="sm" onClick={() => setEditingCharGame(null)} className="h-6 w-6 p-0 text-[#94a3b8]"><X size={14} /></Button>
            </div>
            <p className="text-[10px] text-[#64748b] mb-3">
              Classe: <span className="text-[#e2e8f0] capitalize">{editingCharGame.character?.class_type}</span> · Game ID: <span className="font-mono">{editingCharGame.id?.slice(0,8)}...</span>
            </p>
            <div className="grid grid-cols-2 gap-3">
              {[
                ["level", "Livello"],
                ["xp", "XP"],
                ["stat_points", "Punti Stat."],
                ["gold", "Oro"],
                ["hp", "HP"],
                ["max_hp", "Max HP"],
                ["mana", "Mana"],
                ["max_mana", "Max Mana"],
                ["base_attack", "ATK Base"],
                ["base_defense", "DEF Base"],
              ].map(([k, label]) => (
                <div key={k}>
                  <Label className="text-[10px] text-[#94a3b8]">{label}</Label>
                  <Input
                    type="number"
                    value={charEditForm[k] ?? 0}
                    onChange={e => setCharEditForm({...charEditForm, [k]: e.target.value})}
                    className="h-8 bg-[#0a0a0c] border-[#2d3748] text-[#e2e8f0] text-xs"
                    data-testid={`char-edit-${k}`}
                  />
                </div>
              ))}
            </div>
            <div className="flex gap-2 mt-4">
              <Button onClick={handleSaveCharacter} className="flex-1 bg-[#cfb53b] hover:bg-[#d4af37] text-[#0a0a0c] font-heading text-xs" data-testid="char-edit-save">Salva Modifiche</Button>
              <Button variant="ghost" onClick={() => setEditingCharGame(null)} className="text-[#94a3b8] text-xs">Annulla</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
